import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminProtected from '../components/AdminProtected';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, BookOpen, Loader2, Users, Clock, Target } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// French school IDs
const FRENCH_SCHOOL_IDS = [
  'fondation-solide',
  'la-parole', 
  'vie-victorieuse',
  'evangelisation',
  'apologetique'
];

const EcolesContent = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSchools = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/schools`);
      const data = await response.json();
      // Filter only French schools
      const frenchSchools = data.filter(school => FRENCH_SCHOOL_IDS.includes(school.id));
      setSchools(frenchSchools);
    } catch (error) {
      console.error('Failed to fetch schools:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools();
    
    // Auto-refresh every 10 seconds for real-time updates
    const intervalId = setInterval(() => {
      fetchSchools();
    }, 10000);
    
    // Refetch when page becomes visible (user returns to tab or navigates back)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSchools();
      }
    };
    
    // Refetch on focus (handles navigation back scenarios)
    const handleFocus = () => {
      fetchSchools();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5f7c8a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-14">
        {/* Hero Banner */}
        <div className="bg-[#5f7c8a] py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-light text-white tracking-[0.15em]">
              NOS ÉCOLES
            </h1>
            <div className="w-48 h-1 bg-white/50 mx-auto mt-4" />
          </div>
        </div>

        {/* Back Link */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#5f7c8a] hover:text-[#4a6470] transition-colors"
          >
            <ArrowLeft size={20} />
            Retour à l'accueil
          </Link>
        </div>

        {/* Schools List */}
        <div className="max-w-6xl mx-auto px-4 pb-16">
          <div className="grid gap-6">
            {schools.map((school) => (
              <Link 
                key={school.id}
                to={`/ecole/${school.id}`}
                className="block"
              >
                <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition-all hover:scale-[1.01] group">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-medium text-gray-800 mb-3 group-hover:text-[#5f7c8a] transition-colors">
                        {school.name}
                      </h3>
                      <p className="text-gray-500 mb-4">{school.exam_description}</p>
                      <div className="flex flex-wrap gap-6 text-gray-600">
                        <div className="flex items-center gap-2">
                          <Users size={18} className="text-[#5f7c8a]" />
                          <span>{school.enrolled_students || 0} Étudiants Inscrits</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <BookOpen size={18} className="text-[#5f7c8a]" />
                          <span>{school.mock_exam?.question_count || school.question_count} Questions</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock size={18} className="text-[#5f7c8a]" />
                          <span>{school.mock_exam?.duration_minutes || 12} Minutes</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Target size={18} className="text-[#5f7c8a]" />
                          <span>{school.mock_exam?.passing_score || 60}% pour Réussir</span>
                        </div>
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-4">
                      <Button variant="outline" className="group-hover:bg-[#5f7c8a] group-hover:text-white transition-colors">
                        Voir les détails <ArrowRight size={16} className="ml-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

const EcolesPage = () => {
  return (
    <AdminProtected title="French Schools (Écoles)">
      <EcolesContent />
    </AdminProtected>
  );
};

export default EcolesPage;
